import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Space,
  Button,
  Spin,
  Tag,
  Segmented,
  Select,
  Switch,
  Table,
  Modal,
  Input,
  Typography,
  List,
  message,
  DatePicker,
  Dropdown,
} from 'antd';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  LinkOutlined,
  MessageOutlined,
  PlusOutlined,
  EyeOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import './UnifiedTasksExecutionBoard.css';
import { useNavigate } from 'react-router-dom';
import type { UnifiedTaskRow, UnifiedTaskSlaRisk } from '@/services/unifiedTask.service';
import {
  updateUnifiedTask,
  getTaskComments,
  postTaskComment,
  getTaskHistory,
  createUnifiedTask,
  getUnifiedCompanyUsers,
  type UnifiedCompanyUserRow,
  type UnifiedTaskCommentRow,
  type UnifiedTaskHistoryEntry,
  taskModuleLabel,
} from '@/services/unifiedTask.service';
import { useAuthStore } from '@/store/authStore';

const { Text, Paragraph } = Typography;

const STATUS_COLUMNS = ['Open', 'InProgress', 'Escalated', 'Completed', 'Cancelled'] as const;

const MODULE_OPTIONS = [
  { value: 'Finance', label: 'Finance' },
  { value: 'Sales', label: 'Growth & Revenue' },
  { value: 'SupplyChain', label: 'Supply Chain' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Quality', label: 'Quality' },
  { value: 'Projects', label: 'Projects' },
  { value: 'HR', label: 'HR' },
];

const PRIORITY_OPTIONS = [
  { value: 'Critical', label: 'Critical' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

const TASK_TYPE_FILTER_OPTIONS = [
  { value: 'Manual', label: 'Manual' },
  { value: 'Inspection', label: 'Inspection' },
  { value: 'NCR', label: 'NCR' },
  { value: 'CAPA', label: 'CAPA' },
  { value: 'Approval', label: 'Approval' },
  { value: 'Collection', label: 'Collection' },
  { value: 'Payment', label: 'Payment' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Delivery', label: 'Delivery' },
  { value: 'Follow-up', label: 'Follow-up' },
  { value: 'Receipt', label: 'Receipt' },
  { value: 'Manufacturing', label: 'Manufacturing' },
];

const SOURCE_DOC_FILTER_OPTIONS = [
  { value: 'Manual', label: 'Manual' },
  { value: 'WorkOrder', label: 'Work order' },
  { value: 'DeliveryNote', label: 'Delivery note' },
  { value: 'ApprovalInstance', label: 'Approval' },
  { value: 'SalesOrder', label: 'Customer order' },
  { value: 'PurchaseOrder', label: 'Purchase order' },
  { value: 'SalesInvoice', label: 'Tax invoice' },
  { value: 'SupplierInvoice', label: 'Supplier invoice' },
  { value: 'PaymentVoucher', label: 'Payment voucher' },
  { value: 'JournalVoucher', label: 'Journal voucher' },
  { value: 'GoodsReceiptNote', label: 'GRN' },
  { value: 'PurchaseRequisition', label: 'PR' },
  { value: 'BidAnalysis', label: 'Bid analysis' },
  { value: 'PurchaseReturn', label: 'Purchase return' },
  { value: 'InitialInspectionRequest', label: 'Initial inspection (IIR)' },
  { value: 'FinalInspectionRequest', label: 'Final inspection (FIR)' },
  { value: 'StageCompletionInspection', label: 'Stage inspection' },
  { value: 'NCRRecord', label: 'NCR' },
  { value: 'CAPARecord', label: 'CAPA' },
];

const TASK_ORIGIN_OPTIONS = [
  { value: 'all', label: 'All origins' },
  { value: 'system', label: 'System-generated' },
  { value: 'manual', label: 'Manual only' },
];

const STATUS_FILTER_OPTIONS = STATUS_COLUMNS.map((s) => ({ value: s, label: s }));

function formatSlaLine(t: UnifiedTaskRow): string | null {
  if (!t.expected_completion_at || t.sla_risk === 'none') return null;
  if (t.status === 'Completed' || t.status === 'Cancelled') return null;
  const delay = t.sla_delay_seconds ?? 0;
  if (delay > 0) {
    const h = Math.floor(delay / 3600);
    const m = Math.floor((delay % 3600) / 60);
    return h > 0 ? `${h}h ${m}m over` : `${Math.max(1, m)}m over`;
  }
  const rem = t.sla_remaining_seconds;
  if (rem != null && rem > 0) {
    const h = Math.floor(rem / 3600);
    const m = Math.floor((rem % 3600) / 60);
    return h > 0 ? `${h}h ${m}m left` : `${Math.max(1, m)}m left`;
  }
  return 'At SLA limit';
}

function openActionLink(navigate: ReturnType<typeof useNavigate>, link?: string) {
  if (!link) return;
  if (link.startsWith('http://') || link.startsWith('https://')) {
    window.open(link, '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(link.startsWith('/') ? link : `/${link}`);
}

function dueWithinCalendarDays(t: UnifiedTaskRow, n: number): boolean {
  if (!t.due_date) return false;
  const d = dayjs(t.due_date).startOf('day');
  return d.diff(dayjs().startOf('day'), 'day') >= 0 && d.diff(dayjs().startOf('day'), 'day') <= n;
}

function shouldShowDueChip(t: UnifiedTaskRow): boolean {
  if (!t.due_date) return false;
  if ((t.overdue_days ?? 0) > 0) return true;
  const d = dayjs(t.due_date).startOf('day');
  const days = d.diff(dayjs().startOf('day'), 'day');
  return days >= 0 && days <= 3;
}

function moduleTagClassName(module: string): string {
  const m = module.toLowerCase();
  if (m === 'finance') return 'uteam-mtag uteam-mtag--finance';
  if (m === 'sales') return 'uteam-mtag uteam-mtag--sales';
  if (m === 'supplychain' || m.includes('supply')) return 'uteam-mtag uteam-mtag--scm';
  if (m === 'manufacturing' || m === 'mfg') return 'uteam-mtag uteam-mtag--mfg';
  if (m === 'quality') return 'uteam-mtag uteam-mtag--quality';
  if (m === 'projects' || m === 'project') return 'uteam-mtag uteam-mtag--projects';
  if (m === 'hr') return 'uteam-mtag uteam-mtag--hr';
  return 'uteam-mtag uteam-mtag--other';
}

function getTaskUrgencyClassName(t: UnifiedTaskRow): string {
  if (t.status === 'Completed' || t.status === 'Cancelled') return 'uteam-card--calm uteam-card--done';
  if ((t.overdue_days ?? 0) > 0 || t.sla_risk === 'breach' || t.sla_risk === 'critical') {
    return 'uteam-card--critical';
  }
  if (t.sla_risk === 'warning' || dueWithinCalendarDays(t, 1) || t.status === 'Escalated') {
    return 'uteam-card--high';
  }
  if (t.status === 'InProgress') return 'uteam-card--normal';
  return 'uteam-card--calm';
}

function slaBarModel(t: UnifiedTaskRow): { widthPct: number; color: string } | null {
  if (t.status === 'Completed' || t.status === 'Cancelled') return null;
  if (t.sla_risk === 'none' && !t.expected_completion_at) return null;
  if (t.sla_delay_seconds && t.sla_delay_seconds > 0) {
    return { widthPct: 100, color: 'var(--noble-red, #E63946)' };
  }
  if (!t.expected_completion_at) {
    if (t.sla_risk === 'warning') return { widthPct: 75, color: '#BA7517' };
    if (t.sla_risk === 'safe') return { widthPct: 32, color: 'var(--noble-green, #06D6A0)' };
    return null;
  }
  const start = t.created_at ? new Date(t.created_at).getTime() : Date.now() - 7 * 864e5;
  const end = new Date(t.expected_completion_at).getTime();
  if (end <= start) {
    return { widthPct: 100, color: 'var(--noble-red, #E63946)' };
  }
  const now = Date.now();
  const w = ((now - start) / (end - start)) * 100;
  const widthPct = Math.max(0, Math.min(100, w));
  let color = 'var(--noble-green, #06D6A0)';
  if (widthPct >= 90) color = 'var(--noble-red, #E63946)';
  else if (widthPct >= 60) color = '#BA7517';
  return { widthPct, color };
}

function sortTasksInColumn(a: UnifiedTaskRow, b: UnifiedTaskRow): number {
  const sortKey = (t: UnifiedTaskRow) => {
    if (t.status === 'Completed' || t.status === 'Cancelled') return 4;
    if ((t.overdue_days ?? 0) > 0 || t.sla_risk === 'breach' || t.sla_risk === 'critical') {
      return 0;
    }
    if (t.sla_risk === 'warning' || dueWithinCalendarDays(t, 1)) return 1;
    if (t.status === 'InProgress' || t.status === 'Escalated') return 2;
    return 3;
  };
  const da = sortKey(a);
  const db = sortKey(b);
  if (da !== db) return da - db;
  return (b.execution_priority_score ?? 0) - (a.execution_priority_score ?? 0);
}

const KANBAN_CARD_ESTIMATE_PX = 120;
const KANBAN_COLUMN_OVERSCAN = 8;

function KanbanColumnList({
  columnId,
  rows,
  rowsKey,
  renderRow,
  kbFocusId,
}: {
  columnId: string;
  rows: UnifiedTaskRow[];
  /** Stable id+order signature so scroll effects do not run on every parent re-render. */
  rowsKey: string;
  renderRow: (t: UnifiedTaskRow) => React.ReactNode;
  kbFocusId: string | null;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => KANBAN_CARD_ESTIMATE_PX,
    overscan: KANBAN_COLUMN_OVERSCAN,
  });

  useLayoutEffect(() => {
    if (!kbFocusId) return;
    const idx = rowsRef.current.findIndex((r) => r.id === kbFocusId);
    if (idx < 0) return;
    virtualizer.scrollToIndex(idx, { align: 'auto' });
  }, [kbFocusId, rowsKey, virtualizer]);

  return (
    <div
      ref={parentRef}
      className="uteam-col-list"
      role="list"
      aria-label={`${columnId} column tasks`}
    >
      <div
        className="uteam-col-list__virtual"
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            className="uteam-col-list__row"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {renderRow(rows[virtualItem.index]!)}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface UnifiedTasksExecutionBoardProps {
  tasks: UnifiedTaskRow[];
  loading: boolean;
  onRefresh: () => void;
  moduleFilter?: string;
  onModuleFilterChange?: (v: string | undefined) => void;
  assignedToMe?: boolean;
  onAssignedToMeChange?: (v: boolean) => void;
  overdueOnly?: boolean;
  onOverdueOnlyChange?: (v: boolean) => void;
  dueTodayOnly?: boolean;
  onDueTodayOnlyChange?: (v: boolean) => void;
  noDueDateOnly?: boolean;
  onNoDueDateOnlyChange?: (v: boolean) => void;
  priorityFilter?: string;
  onPriorityFilterChange?: (v: string | undefined) => void;
  hideAssignedToMeToggle?: boolean;
  hideModuleFilter?: boolean;
  allowManualCreate?: boolean;
  titleExtra?: React.ReactNode;
  searchQuery?: string;
  onSearchQueryChange?: (q: string) => void;
  unassignedOnly?: boolean;
  onUnassignedOnlyChange?: (v: boolean) => void;
  teamOthersOnly?: boolean;
  onTeamOthersOnlyChange?: (v: boolean) => void;
  taskTypeFilter?: string;
  onTaskTypeFilterChange?: (v: string | undefined) => void;
  sourceDocumentTypeFilter?: string;
  onSourceDocumentTypeFilterChange?: (v: string | undefined) => void;
  taskOriginFilter?: 'all' | 'system' | 'manual';
  onTaskOriginFilterChange?: (v: 'all' | 'system' | 'manual') => void;
  statusFilter?: string;
  onStatusFilterChange?: (v: string | undefined) => void;
  openTasksOnly?: boolean;
  onOpenTasksOnlyChange?: (v: boolean) => void;
  /** Execution control filters (omit handlers to hide the group) */
  blockedOnly?: boolean;
  onBlockedOnlyChange?: (v: boolean) => void;
  readyToStart?: boolean;
  onReadyToStartChange?: (v: boolean) => void;
  slaRiskFilter?: 'all' | UnifiedTaskSlaRisk;
  onSlaRiskFilterChange?: (v: 'all' | UnifiedTaskSlaRisk) => void;
  sortBySla?: boolean;
  onSortBySlaChange?: (v: boolean) => void;
  boardTitle?: string;
  boardDescription?: string;
}

export const UnifiedTasksExecutionBoard: React.FC<UnifiedTasksExecutionBoardProps> = ({
  tasks,
  loading,
  onRefresh,
  moduleFilter,
  onModuleFilterChange,
  assignedToMe,
  onAssignedToMeChange,
  overdueOnly,
  onOverdueOnlyChange,
  dueTodayOnly,
  onDueTodayOnlyChange,
  noDueDateOnly,
  onNoDueDateOnlyChange,
  priorityFilter,
  onPriorityFilterChange,
  hideAssignedToMeToggle,
  hideModuleFilter,
  allowManualCreate,
  titleExtra,
  searchQuery,
  onSearchQueryChange,
  unassignedOnly,
  onUnassignedOnlyChange,
  teamOthersOnly,
  onTeamOthersOnlyChange,
  taskTypeFilter,
  onTaskTypeFilterChange,
  sourceDocumentTypeFilter,
  onSourceDocumentTypeFilterChange,
  taskOriginFilter,
  onTaskOriginFilterChange,
  statusFilter,
  onStatusFilterChange,
  openTasksOnly,
  onOpenTasksOnlyChange,
  blockedOnly,
  onBlockedOnlyChange,
  readyToStart,
  onReadyToStartChange,
  slaRiskFilter,
  onSlaRiskFilterChange,
  sortBySla,
  onSortBySlaChange,
  boardTitle = 'Execution tasks',
  boardDescription,
}) => {
  const navigate = useNavigate();
  const detailRef = useRef<HTMLDivElement | null>(null);
  const statusUndoKeyRef = useRef(0);
  const [view, setView] = useState<'board' | 'table'>('board');
  const [notesTask, setNotesTask] = useState<UnifiedTaskRow | null>(null);
  const [comments, setComments] = useState<UnifiedTaskCommentRow[]>([]);
  const [history, setHistory] = useState<UnifiedTaskHistoryEntry[]>([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createModule, setCreateModule] = useState<string>('Sales');
  const [createPriority, setCreatePriority] = useState<string>('Medium');
  const [createDueDate, setCreateDueDate] = useState<dayjs.Dayjs | null>(null);
  const [createAssignee, setCreateAssignee] = useState<string | undefined>(undefined);
  const [createSaving, setCreateSaving] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<UnifiedCompanyUserRow[]>([]);
  const [notesAssignee, setNotesAssignee] = useState<string | undefined>(undefined);
  const [notesAssignSaving, setNotesAssignSaving] = useState(false);
  const [notesRemarksDraft, setNotesRemarksDraft] = useState('');
  const [notesRemarksSaving, setNotesRemarksSaving] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQ, setCommandQ] = useState('');
  const [commandIdx, setCommandIdx] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [kbFocusId, setKbFocusId] = useState<string | null>(null);
  const commandInputRef = useRef<HTMLInputElement | null>(null);
  const [taskPatches, setTaskPatches] = useState<Record<string, Partial<UnifiedTaskRow>>>({});
  const [justSynced, setJustSynced] = useState(false);
  const refreshFromUserRef = useRef(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const lastRangeAnchorRef = useRef<string | null>(null);
  const meUser = useAuthStore((s) => s.user);
  const meId = meUser?.id;
  const meDisplayName = meUser
    ? [meUser.first_name, meUser.last_name].filter(Boolean).join(' ').trim() || meUser.username
    : undefined;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getUnifiedCompanyUsers();
        if (!cancelled) setCompanyUsers(rows);
      } catch {
        if (!cancelled) setCompanyUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userSelectOptions = useMemo(
    () =>
      companyUsers.map((u) => ({
        value: u.id,
        label: u.email ? `${u.name} (${u.email})` : u.name,
      })),
    [companyUsers]
  );

  const displayTasks = useMemo(
    () => tasks.map((t) => ({ ...t, ...taskPatches[t.id] })),
    [tasks, taskPatches]
  );

  useEffect(() => {
    setTaskPatches((p) => {
      if (Object.keys(p).length === 0) return p;
      const next = { ...p };
      for (const id of Object.keys(next)) {
        const s = tasks.find((t) => t.id === id);
        if (!s) {
          delete next[id];
          continue;
        }
        const pat = next[id]!;
        let match = true;
        if (pat.status != null && pat.status !== s.status) match = false;
        if (pat.assigned_to !== undefined) {
          const sa = s.assigned_to ?? null;
          const pa = pat.assigned_to ?? null;
          if (pa !== sa) match = false;
        }
        if (match) delete next[id];
      }
      return next;
    });
  }, [tasks]);

  useEffect(() => {
    setNotesTask((prev) => {
      if (!prev) return prev;
      const s = displayTasks.find((t) => t.id === prev.id);
      if (!s) return prev;
      if (s.status === prev.status && (s.assigned_to ?? null) === (prev.assigned_to ?? null)) return prev;
      return { ...prev, status: s.status, assigned_to: s.assigned_to, assigned_to_name: s.assigned_to_name };
    });
  }, [displayTasks]);

  const triggerRefresh = useCallback(() => {
    refreshFromUserRef.current = true;
    onRefresh();
  }, [onRefresh]);

  useEffect(() => {
    if (loading) return;
    if (!refreshFromUserRef.current) return;
    refreshFromUserRef.current = false;
    setJustSynced(true);
    const tid = window.setTimeout(() => setJustSynced(false), 2000);
    return () => clearTimeout(tid);
  }, [loading]);

  useEffect(() => {
    setSelectedTaskIds([]);
  }, [view]);

  const sortedGrouped = useMemo(() => {
    const map = new Map<string, UnifiedTaskRow[]>();
    for (const s of STATUS_COLUMNS) map.set(s, []);
    for (const t of displayTasks) {
      const key = STATUS_COLUMNS.includes(t.status as (typeof STATUS_COLUMNS)[number]) ? t.status : 'Open';
      map.get(key)!.push(t);
    }
    for (const s of STATUS_COLUMNS) {
      map.get(s)!.sort(sortTasksInColumn);
    }
    return map;
  }, [displayTasks]);

  const boardMetricChips = useMemo(() => {
    const overdue = displayTasks.filter((t) => (t.overdue_days ?? 0) > 0).length;
    const inFlight = displayTasks.filter(
      (t) => t.status === 'Open' || t.status === 'InProgress' || t.status === 'Escalated'
    ).length;
    const done = displayTasks.filter((t) => t.status === 'Completed').length;
    return { overdue, inFlight, done };
  }, [displayTasks]);

  const filterTokens = useMemo(() => {
    type T = { key: string; label: string; onClear: () => void; kind?: 'danger' | 'info' };
    const out: T[] = [];
    if (overdueOnly && onOverdueOnlyChange) {
      out.push({
        key: 'overdue',
        kind: 'danger',
        label: 'Overdue only',
        onClear: () => onOverdueOnlyChange(false),
      });
    }
    if (unassignedOnly && onUnassignedOnlyChange) {
      out.push({ key: 'unassigned', kind: 'info', label: 'Unassigned', onClear: () => onUnassignedOnlyChange(false) });
    }
    if (moduleFilter && onModuleFilterChange) {
      out.push({ key: 'module', label: `Module: ${moduleFilter}`, onClear: () => onModuleFilterChange(undefined) });
    }
    if (statusFilter && onStatusFilterChange) {
      out.push({ key: 'status', label: `Status: ${statusFilter}`, onClear: () => onStatusFilterChange(undefined) });
    }
    if (priorityFilter && onPriorityFilterChange) {
      out.push({ key: 'pr', label: `Priority: ${priorityFilter}`, onClear: () => onPriorityFilterChange(undefined) });
    }
    if (taskTypeFilter && onTaskTypeFilterChange) {
      out.push({ key: 'tt', label: `Type: ${taskTypeFilter}`, onClear: () => onTaskTypeFilterChange(undefined) });
    }
    if (sourceDocumentTypeFilter && onSourceDocumentTypeFilterChange) {
      out.push({
        key: 'sdoc',
        label: `Source: ${sourceDocumentTypeFilter}`,
        onClear: () => onSourceDocumentTypeFilterChange(undefined),
      });
    }
    if (taskOriginFilter && taskOriginFilter !== 'all' && onTaskOriginFilterChange) {
      out.push({ key: 'or', label: `Origin: ${taskOriginFilter}`, onClear: () => onTaskOriginFilterChange('all') });
    }
    if (dueTodayOnly && onDueTodayOnlyChange) {
      out.push({ key: 'due', label: 'Due today', onClear: () => onDueTodayOnlyChange(false) });
    }
    if (noDueDateOnly && onNoDueDateOnlyChange) {
      out.push({ key: 'nodue', label: 'No due date', onClear: () => onNoDueDateOnlyChange(false) });
    }
    if (openTasksOnly && onOpenTasksOnlyChange) {
      out.push({ key: 'openo', label: 'Open only', onClear: () => onOpenTasksOnlyChange(false) });
    }
    if (blockedOnly && onBlockedOnlyChange) {
      out.push({ key: 'bl', label: 'Blocked', onClear: () => onBlockedOnlyChange(false) });
    }
    if (readyToStart && onReadyToStartChange) {
      out.push({ key: 'rts', label: 'Ready', onClear: () => onReadyToStartChange(false) });
    }
    if (slaRiskFilter && slaRiskFilter !== 'all' && onSlaRiskFilterChange) {
      out.push({ key: 'sla', label: `SLA: ${slaRiskFilter}`, onClear: () => onSlaRiskFilterChange('all') });
    }
    if (sortBySla && onSortBySlaChange) {
      out.push({ key: 'srt', label: 'Sort: SLA', onClear: () => onSortBySlaChange(false) });
    }
    if (searchQuery && searchQuery.trim() && onSearchQueryChange) {
      out.push({ key: 'q', label: `Search: ${searchQuery.trim().slice(0, 40)}`, onClear: () => onSearchQueryChange('') });
    }
    return out;
  }, [
    overdueOnly,
    onOverdueOnlyChange,
    unassignedOnly,
    onUnassignedOnlyChange,
    moduleFilter,
    onModuleFilterChange,
    statusFilter,
    onStatusFilterChange,
    priorityFilter,
    onPriorityFilterChange,
    taskTypeFilter,
    onTaskTypeFilterChange,
    sourceDocumentTypeFilter,
    onSourceDocumentTypeFilterChange,
    taskOriginFilter,
    onTaskOriginFilterChange,
    dueTodayOnly,
    onDueTodayOnlyChange,
    noDueDateOnly,
    onNoDueDateOnlyChange,
    openTasksOnly,
    onOpenTasksOnlyChange,
    blockedOnly,
    onBlockedOnlyChange,
    readyToStart,
    onReadyToStartChange,
    slaRiskFilter,
    onSlaRiskFilterChange,
    sortBySla,
    onSortBySlaChange,
    searchQuery,
    onSearchQueryChange,
  ]);

  const taskById = useMemo(() => new Map(displayTasks.map((t) => [t.id, t] as const)), [displayTasks]);

  const boardFlatOrder = useMemo(() => {
    const ids: string[] = [];
    for (const col of STATUS_COLUMNS) {
      for (const row of sortedGrouped.get(col) ?? []) ids.push(row.id);
    }
    return ids;
  }, [sortedGrouped]);

  const selectedSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);

  const addRangeToSelection = useCallback(
    (fromId: string, toId: string) => {
      const order = boardFlatOrder;
      const i0 = order.indexOf(fromId);
      const i1 = order.indexOf(toId);
      if (i0 < 0 || i1 < 0) return;
      const lo = Math.min(i0, i1);
      const hi = Math.max(i0, i1);
      const rangeIds = order.slice(lo, hi + 1);
      setSelectedTaskIds((prev) => {
        const s = new Set(prev);
        for (const id of rangeIds) s.add(id);
        return Array.from(s);
      });
    },
    [boardFlatOrder]
  );

  const commandResults = useMemo(() => {
    const q = commandQ.trim().toLowerCase();
    const list = !q
      ? displayTasks
      : displayTasks.filter((t) => {
          const parts = [t.recommended_action, t.task_type, t.source_document_number, t.party_display_name, t.assigned_to_name, t.module]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return parts.includes(q);
        });
    return list.slice(0, 8);
  }, [displayTasks, commandQ]);

  const loadNotes = useCallback(async (task: UnifiedTaskRow) => {
    setCommentLoading(true);
    try {
      const [c, h] = await Promise.all([getTaskComments(task.id), getTaskHistory(task.id)]);
      setComments(c);
      setHistory(h);
    } catch {
      setComments([]);
      setHistory([]);
    } finally {
      setCommentLoading(false);
    }
  }, []);

  const handleOpenNotes = (task: UnifiedTaskRow) => {
    if (notesTask?.id === task.id) {
      setNotesTask(null);
      return;
    }
    setNotesTask(task);
    setNotesAssignee(task.assigned_to ?? undefined);
    setNotesRemarksDraft(task.remarks ?? '');
    setNewComment('');
    void loadNotes(task);
  };

  useEffect(() => {
    if (!notesTask) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (commandOpen || helpOpen) return;
        e.preventDefault();
        setNotesTask(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notesTask, commandOpen, helpOpen]);

  const saveNotesRemarks = async () => {
    if (!notesTask) return;
    const trimmed = notesRemarksDraft.trim();
    const prev = (notesTask.remarks ?? '').trim();
    if (trimmed === prev) {
      message.info('No changes to save');
      return;
    }
    setNotesRemarksSaving(true);
    try {
      const updated = await updateUnifiedTask(notesTask.id, { remarks: trimmed });
      if (updated) setNotesTask(updated);
      message.success('Remarks saved');
      onRefresh();
    } catch {
      message.error('Could not save remarks');
    } finally {
      setNotesRemarksSaving(false);
    }
  };

  const submitComment = async () => {
    if (!notesTask || !newComment.trim()) return;
    try {
      await postTaskComment(notesTask.id, newComment.trim());
      setNewComment('');
      await loadNotes(notesTask);
      message.success('Comment added');
    } catch {
      message.error('Failed to add comment');
    }
  };

  const handleStatusChange = async (task: UnifiedTaskRow, status: string) => {
    if (status === task.status) return;
    const previous = task.status;
    const id = task.id;
    setTaskPatches((p) => ({ ...p, [id]: { ...p[id], status: status as UnifiedTaskRow['status'] } }));
    const undoKey = `utask-status-undo-${task.id}-${++statusUndoKeyRef.current}`;
    try {
      await updateUnifiedTask(task.id, { status });
      onRefresh();
      message.open({
        type: 'success',
        key: undoKey,
        duration: 5,
        content: (
          <span>
            Status updated.{' '}
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', lineHeight: 1.2 }}
              onClick={() => {
                void (async () => {
                  try {
                    await updateUnifiedTask(task.id, { status: previous });
                    message.destroy(undoKey);
                    onRefresh();
                  } catch {
                    message.error('Undo failed');
                  }
                })();
              }}
            >
              Undo
            </Button>
          </span>
        ),
      });
    } catch (e: unknown) {
      setTaskPatches((p) => {
        const n = { ...p };
        const cur = n[id];
        if (!cur) return p;
        const { status: _s, ...rest } = cur;
        if (Object.keys(rest).length === 0) delete n[id];
        else n[id] = rest;
        return n;
      });
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      const msg =
        ax?.response?.data?.message || (ax?.response?.data as { error?: string })?.error || ax?.message || 'Update failed';
      message.error(typeof msg === 'string' ? msg : 'Update failed');
    }
  };

  const handleAssigneeChange = async (task: UnifiedTaskRow, userId: string | null) => {
    const id = task.id;
    const name = userId ? companyUsers.find((u) => u.id === userId)?.name : undefined;
    setTaskPatches((p) => ({
      ...p,
      [id]: { ...p[id], assigned_to: userId ?? undefined, assigned_to_name: name },
    }));
    try {
      await updateUnifiedTask(task.id, { assigned_to: userId });
      message.success('Assignee updated');
      onRefresh();
    } catch {
      setTaskPatches((p) => {
        const n = { ...p };
        const cur = n[id];
        if (!cur) return p;
        const { assigned_to: _a, assigned_to_name: _n, ...rest } = cur;
        if (Object.keys(rest).length === 0) delete n[id];
        else n[id] = rest;
        return n;
      });
      message.error('Update failed');
    }
  };

  const applyNotesAssignee = async () => {
    if (!notesTask) return;
    const id = notesTask.id;
    const nextAssign = notesAssignee === undefined ? null : notesAssignee;
    const name = nextAssign ? companyUsers.find((u) => u.id === nextAssign)?.name : undefined;
    setTaskPatches((p) => ({
      ...p,
      [id]: { ...p[id], assigned_to: nextAssign ?? undefined, assigned_to_name: name },
    }));
    setNotesAssignSaving(true);
    try {
      const updated = await updateUnifiedTask(notesTask.id, {
        assigned_to: notesAssignee === undefined ? null : notesAssignee,
      });
      if (updated) setNotesTask(updated);
      message.success('Assignee updated');
      onRefresh();
    } catch {
      setTaskPatches((p) => {
        const n = { ...p };
        const cur = n[id];
        if (!cur) return p;
        const { assigned_to: _a, assigned_to_name: _n, ...rest } = cur;
        if (Object.keys(rest).length === 0) delete n[id];
        else n[id] = rest;
        return n;
      });
      message.error('Could not update assignee');
    } finally {
      setNotesAssignSaving(false);
    }
  };

  const submitCreate = async () => {
    if (!createTitle.trim()) {
      message.warning('Enter a task description');
      return;
    }
    setCreateSaving(true);
    try {
      await createUnifiedTask({
        recommended_action: createTitle.trim(),
        module: createModule,
        priority: createPriority,
        due_date: createDueDate ? createDueDate.format('YYYY-MM-DD') : undefined,
        assigned_to: createAssignee ?? null,
      });
      message.success('Task created');
      setCreateOpen(false);
      setCreateTitle('');
      setCreateDueDate(null);
      setCreateAssignee(undefined);
      onRefresh();
    } catch {
      message.error('Could not create task');
    } finally {
      setCreateSaving(false);
    }
  };

  const copyTaskLink = useCallback((id: string) => {
    const path = `${window.location.origin}/home/tasks/${id}`;
    void navigator.clipboard.writeText(path);
    message.success('Task link copied');
  }, []);

  const handleAssignToMe = useCallback(
    async (t: UnifiedTaskRow) => {
      if (!meId) {
        message.warning('Not signed in');
        return;
      }
      const id = t.id;
      setTaskPatches((p) => ({
        ...p,
        [id]: { ...p[id], assigned_to: meId, assigned_to_name: meDisplayName },
      }));
      try {
        await updateUnifiedTask(t.id, { assigned_to: meId });
        message.success('Assigned to you');
        onRefresh();
      } catch {
        setTaskPatches((p) => {
          const n = { ...p };
          const cur = n[id];
          if (!cur) return p;
          const { assigned_to: _a, assigned_to_name: _n, ...rest } = cur;
          if (Object.keys(rest).length === 0) delete n[id];
          else n[id] = rest;
          return n;
        });
        message.error('Update failed');
      }
    },
    [meId, meDisplayName, onRefresh]
  );

  const runBulkStatusChange = useCallback(
    async (targetStatus: string) => {
      const ids = selectedTaskIds.filter((id) => {
        const row = taskById.get(id);
        return row && row.status !== targetStatus;
      });
      if (ids.length === 0) {
        message.info('No tasks to update');
        return;
      }
      setTaskPatches((p) => {
        const n = { ...p };
        for (const id of ids) {
          n[id] = { ...n[id], status: targetStatus as UnifiedTaskRow['status'] };
        }
        return n;
      });
      const results = await Promise.allSettled(ids.map((id) => updateUnifiedTask(id, { status: targetStatus })));
      const failed: string[] = [];
      results.forEach((r, i) => {
        if (r.status === 'rejected') failed.push(ids[i]!);
      });
      if (failed.length) {
        setTaskPatches((p) => {
          const n = { ...p };
          for (const id of failed) {
            const cur = n[id];
            if (!cur) continue;
            const { status: _s, ...rest } = cur;
            if (Object.keys(rest).length === 0) delete n[id];
            else n[id] = rest;
          }
          return n;
        });
      }
      if (failed.length === ids.length) message.error('Could not update tasks');
      else if (failed.length) message.warning(`Updated ${ids.length - failed.length} of ${ids.length} tasks`);
      else message.success(`Updated ${ids.length} task${ids.length === 1 ? '' : 's'}`);
      setSelectedTaskIds([]);
      onRefresh();
    },
    [selectedTaskIds, taskById, onRefresh]
  );

  const runBulkAssignToMe = useCallback(async () => {
    if (!meId) {
      message.warning('Not signed in');
      return;
    }
    const ids = selectedTaskIds.filter((id) => {
      const row = taskById.get(id);
      return row && (row.assigned_to ?? null) !== (meId ?? null);
    });
    if (ids.length === 0) {
      message.info('No tasks to assign');
      return;
    }
    setTaskPatches((p) => {
      const n = { ...p };
      for (const id of ids) {
        n[id] = { ...n[id], assigned_to: meId, assigned_to_name: meDisplayName };
      }
      return n;
    });
    const results = await Promise.allSettled(ids.map((id) => updateUnifiedTask(id, { assigned_to: meId })));
    const failed: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') failed.push(ids[i]!);
    });
    if (failed.length) {
      setTaskPatches((p) => {
        const n = { ...p };
        for (const id of failed) {
          const cur = n[id];
          if (!cur) continue;
          const { assigned_to: _a, assigned_to_name: _n, ...rest } = cur;
          if (Object.keys(rest).length === 0) delete n[id];
          else n[id] = rest;
        }
        return n;
      });
    }
    if (failed.length === ids.length) message.error('Could not assign tasks');
    else if (failed.length) message.warning(`Assigned ${ids.length - failed.length} of ${ids.length} tasks`);
    else message.success(`Assigned ${ids.length} task${ids.length === 1 ? '' : 's'} to you`);
    setSelectedTaskIds([]);
    onRefresh();
  }, [selectedTaskIds, taskById, meId, meDisplayName, onRefresh]);

  const getCardContextMenu = useCallback(
    (t: UnifiedTaskRow): MenuProps['items'] => {
      const items: MenuProps['items'] = [
        { key: 'me', label: 'Assign to me', disabled: !meId },
        { type: 'divider' as const },
        { key: 'copy', label: 'Copy task link' },
      ];
      if (t.action_link) {
        items.push({ key: 'osrc', label: 'Open source doc' });
      }
      items.push({
        type: 'submenu' as const,
        key: 'st',
        label: 'Change status',
        children: STATUS_COLUMNS.map((s) => ({ key: `s:${s}`, label: s })),
      });
      return items;
    },
    [meId]
  );

  const onCardContextMenu = useCallback(
    ({ key }: { key: string }, t: UnifiedTaskRow) => {
      if (key === 'me') {
        void handleAssignToMe(t);
        return;
      }
      if (key === 'copy') {
        copyTaskLink(t.id);
        return;
      }
      if (key === 'osrc') {
        openActionLink(navigate, t.action_link);
        return;
      }
      if (key.startsWith('s:')) {
        void handleStatusChange(t, key.slice(2));
      }
    },
    [copyTaskLink, handleAssignToMe, navigate, handleStatusChange]
  );

  const isTextInputTarget = (target: EventTarget | null) => {
    if (!target || !(target instanceof HTMLElement)) return false;
    const n = target.tagName;
    return n === 'INPUT' || n === 'TEXTAREA' || n === 'SELECT' || target.isContentEditable;
  };

  useEffect(() => {
    if (kbFocusId && !boardFlatOrder.includes(kbFocusId)) {
      setKbFocusId(null);
    }
  }, [boardFlatOrder, kbFocusId]);

  useEffect(() => {
    if (commandOpen) {
      setCommandIdx(0);
    }
  }, [commandQ, commandOpen]);

  useEffect(() => {
    if (!commandOpen) return;
    const max = Math.max(0, commandResults.length - 1);
    setCommandIdx((i) => (i > max ? 0 : i));
  }, [commandOpen, commandResults.length]);

  useEffect(() => {
    if (!commandOpen) return;
    const t = setTimeout(() => commandInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [commandOpen]);

  useEffect(() => {
    if (!commandOpen) return;
    const onK = (e: KeyboardEvent) => {
      const n = commandResults.length;
      if (e.key === 'Escape') {
        e.preventDefault();
        setCommandOpen(false);
        return;
      }
      if (n === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCommandIdx((i) => Math.min(i + 1, n - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCommandIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        const tsk = commandResults[commandIdx];
        if (tsk) {
          e.preventDefault();
          setCommandOpen(false);
          setCommandQ('');
          handleOpenNotes(tsk);
        }
      }
    };
    window.addEventListener('keydown', onK, true);
    return () => window.removeEventListener('keydown', onK, true);
  }, [commandOpen, commandResults, commandIdx, handleOpenNotes]);

  useEffect(() => {
    const onK = (e: KeyboardEvent) => {
      if (createOpen) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandQ('');
        setCommandOpen((o) => !o);
        return;
      }
    };
    window.addEventListener('keydown', onK, true);
    return () => window.removeEventListener('keydown', onK, true);
  }, [createOpen]);

  useEffect(() => {
    const onK = (e: KeyboardEvent) => {
      if (createOpen || commandOpen || helpOpen) return;
      if (e.key === 'Escape' && selectedTaskIds.length > 0 && !notesTask) {
        if (isTextInputTarget(e.target)) return;
        e.preventDefault();
        setSelectedTaskIds([]);
        return;
      }
      if (view !== 'board') return;
      if (isTextInputTarget(e.target)) return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        triggerRefresh();
        return;
      }
      if ((e.key === 'n' || e.key === 'N') && allowManualCreate) {
        e.preventDefault();
        setCreateOpen(true);
        return;
      }
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        const t = kbFocusId ? taskById.get(kbFocusId) : null;
        if (t) void handleAssignToMe(t);
        return;
      }
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        const t = kbFocusId ? taskById.get(kbFocusId) : null;
        if (t) void handleStatusChange(t, 'Completed');
        return;
      }
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        const t = kbFocusId ? taskById.get(kbFocusId) : null;
        if (t) void handleStatusChange(t, 'Escalated');
        return;
      }
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        if (boardFlatOrder.length === 0) return;
        if (!kbFocusId) {
          setKbFocusId(boardFlatOrder[0] ?? null);
          return;
        }
        const idx = boardFlatOrder.indexOf(kbFocusId);
        if (idx < 0) {
          setKbFocusId(boardFlatOrder[0] ?? null);
          return;
        }
        setKbFocusId(boardFlatOrder[Math.min(idx + 1, boardFlatOrder.length - 1)] ?? null);
        return;
      }
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        if (boardFlatOrder.length === 0) return;
        if (!kbFocusId) {
          setKbFocusId(boardFlatOrder[0] ?? null);
          return;
        }
        const idx = boardFlatOrder.indexOf(kbFocusId);
        if (idx <= 0) return;
        setKbFocusId(boardFlatOrder[Math.max(0, idx - 1)] ?? null);
        return;
      }
      if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        if (!kbFocusId) {
          for (let c = STATUS_COLUMNS.length - 1; c >= 0; c--) {
            const id = (sortedGrouped.get(STATUS_COLUMNS[c]) ?? [])[0]?.id;
            if (id) {
              setKbFocusId(id);
              return;
            }
          }
          return;
        }
        let c = 0;
        for (let i = 0; i < STATUS_COLUMNS.length; i++) {
          if ((sortedGrouped.get(STATUS_COLUMNS[i]) ?? []).some((x) => x.id === kbFocusId)) {
            c = i;
            break;
          }
        }
        for (let u = c - 1; u >= 0; u--) {
          const id = (sortedGrouped.get(STATUS_COLUMNS[u]) ?? [])[0]?.id;
          if (id) {
            setKbFocusId(id);
            return;
          }
        }
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        if (!kbFocusId) {
          for (let c = 0; c < STATUS_COLUMNS.length; c++) {
            const id = (sortedGrouped.get(STATUS_COLUMNS[c]) ?? [])[0]?.id;
            if (id) {
              setKbFocusId(id);
              return;
            }
          }
          return;
        }
        let c = 0;
        for (let i = 0; i < STATUS_COLUMNS.length; i++) {
          if ((sortedGrouped.get(STATUS_COLUMNS[i]) ?? []).some((x) => x.id === kbFocusId)) {
            c = i;
            break;
          }
        }
        for (let u = c + 1; u < STATUS_COLUMNS.length; u++) {
          const id = (sortedGrouped.get(STATUS_COLUMNS[u]) ?? [])[0]?.id;
          if (id) {
            setKbFocusId(id);
            return;
          }
        }
        return;
      }
      if (e.key === ' ' && kbFocusId) {
        e.preventDefault();
        const t = taskById.get(kbFocusId);
        if (t) {
          if (notesTask?.id === t.id) return;
          handleOpenNotes(t);
        }
        return;
      }
    };
    window.addEventListener('keydown', onK, true);
    return () => window.removeEventListener('keydown', onK, true);
  }, [
    view,
    createOpen,
    commandOpen,
    helpOpen,
    boardFlatOrder,
    kbFocusId,
    notesTask,
    triggerRefresh,
    sortedGrouped,
    taskById,
    handleOpenNotes,
    allowManualCreate,
    handleAssignToMe,
    handleStatusChange,
    selectedTaskIds.length,
  ]);

  useEffect(() => {
    if (!kbFocusId) return;
    if (view === 'board') return;
    const el = document.getElementById(`uteam-focus-${kbFocusId}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [kbFocusId, view, sortedGrouped]);

  const tableColumns: ColumnsType<UnifiedTaskRow> = [
    { title: 'Action', dataIndex: 'recommended_action', key: 'recommended_action', ellipsis: true },
    { title: 'Module', dataIndex: 'module', key: 'module', width: 100 },
    { title: 'Type', dataIndex: 'task_type', key: 'task_type', width: 100 },
    {
      title: 'Doc #',
      dataIndex: 'source_document_number',
      key: 'source_document_number',
      width: 110,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'Party',
      dataIndex: 'party_display_name',
      key: 'party_display_name',
      ellipsis: true,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 140,
      ellipsis: true,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'Assignee',
      dataIndex: 'assigned_to_name',
      key: 'assigned_to_name',
      width: 168,
      render: (_: unknown, r: UnifiedTaskRow) => (
        <Select
          size="small"
          style={{ width: 160 }}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Unassigned"
          value={r.assigned_to}
          options={userSelectOptions}
          onChange={(v) => void handleAssigneeChange(r, v ?? null)}
        />
      ),
    },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90 },
    {
      title: 'SLA',
      key: 'sla',
      width: 112,
      sorter: (a, b) => (a.execution_priority_score ?? 0) - (b.execution_priority_score ?? 0),
      render: (_: unknown, r: UnifiedTaskRow) => {
        const line = formatSlaLine(r);
        if (!line) return '—';
        const color =
          r.sla_risk === 'critical' ? 'magenta' : r.sla_risk === 'breach' ? 'red' : r.sla_risk === 'warning' ? 'orange' : 'green';
        return <Tag color={color}>{line}</Tag>;
      },
    },
    {
      title: 'Score',
      dataIndex: 'execution_priority_score',
      key: 'execution_priority_score',
      width: 72,
      sorter: (a, b) => (a.execution_priority_score ?? 0) - (b.execution_priority_score ?? 0),
      render: (v: number | undefined) => (v != null ? v : '—'),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Due',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 100,
      render: (d: string | undefined, r) =>
        d ? (
          <span>
            {d}
            {(r.overdue_days ?? 0) > 0 ? <Tag color="red">{r.overdue_days}d</Tag> : null}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 268,
      render: (_: unknown, r) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => openActionLink(navigate, r.action_link)}>
            Open
          </Button>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/home/tasks/${r.id}`)}>
            Details
          </Button>
          <Select
            size="small"
            style={{ width: 120 }}
            value={r.status}
            options={STATUS_COLUMNS.map((s) => ({ value: s, label: s }))}
            onChange={(v) => void handleStatusChange(r, v)}
          />
          <Button type="link" size="small" icon={<MessageOutlined />} onClick={() => handleOpenNotes(r)}>
            Notes{r.comment_count ? ` (${r.comment_count})` : ''}
          </Button>
        </Space>
      ),
    },
  ];

  const statusChipForCard = (t: UnifiedTaskRow) => {
    if (t.status === 'InProgress') return <Tag color="blue">{t.status}</Tag>;
    if (t.status === 'Escalated') return <Tag color="orange">{t.status}</Tag>;
    if (t.status === 'Open') return <Tag>{t.status}</Tag>;
    if (t.status === 'Completed') return <Tag color="success">{t.status}</Tag>;
    if (t.status === 'Cancelled') return <Tag color="default">{t.status}</Tag>;
    return <Tag>{t.status}</Tag>;
  };

  const assigneeMonogram = (t: UnifiedTaskRow) => {
    if (!t.assigned_to_name) return null;
    const s = t.assigned_to_name.trim();
    if (!s) return null;
    return s
      .split(/\s+/)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const renderCard = (t: UnifiedTaskRow) => {
    const slaB = slaBarModel(t);
    const showDue = shouldShowDueChip(t);
    const monogram = assigneeMonogram(t);
    const stop = (e: React.SyntheticEvent) => e.stopPropagation();
    return (
      <Dropdown
        key={t.id}
        trigger={['contextMenu']}
        menu={{
          items: getCardContextMenu(t),
          onClick: (info) => onCardContextMenu({ key: String(info.key) }, t),
        }}
      >
        <div
          id={`uteam-focus-${t.id}`}
          role="listitem"
          aria-selected={selectedSet.has(t.id)}
          className={`uteam-card${notesTask?.id === t.id ? ' uteam-card--selected' : ''}${
            selectedSet.has(t.id) ? ' uteam-card--bulk' : ''
          }${kbFocusId === t.id ? ' uteam-card--kb' : ''} ${getTaskUrgencyClassName(t)}`}
          onClick={(e) => {
            setKbFocusId(t.id);
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              setSelectedTaskIds((prev) => (prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]));
              lastRangeAnchorRef.current = t.id;
              return;
            }
            if (e.shiftKey && lastRangeAnchorRef.current) {
              e.preventDefault();
              addRangeToSelection(lastRangeAnchorRef.current, t.id);
              return;
            }
            lastRangeAnchorRef.current = t.id;
            setSelectedTaskIds([]);
            handleOpenNotes(t);
          }}
          aria-label={`${t.recommended_action || t.task_type || 'Task'}`}
        >
        <div className="uteam-card__title-row">
          <div className="uteam-card__title">{t.recommended_action || t.task_type}</div>
          {statusChipForCard(t)}
        </div>
        <div className="uteam-meta">
          {t.source_document_number ? <span>{t.source_document_number}</span> : null}
          {t.source_document_number && t.party_display_name ? ' · ' : null}
          {t.party_display_name ? <span title={t.party_display_name}>{t.party_display_name}</span> : null}
        </div>
        <div className="uteam-card-tags">
          <span className={moduleTagClassName(t.module || '')} title="Module">
            {taskModuleLabel(t.module)}
          </span>
          {showDue && t.due_date ? (
            <Tag color={(t.overdue_days ?? 0) > 0 ? 'red' : 'orange'} style={{ margin: 0, fontSize: 11, fontWeight: 500 }}>
              {(t.overdue_days ?? 0) > 0 ? `Overdue ${t.overdue_days}d` : `Due ${t.due_date}`}
            </Tag>
          ) : null}
          {formatSlaLine(t) ? (
            <Tag
              color={
                t.sla_risk === 'critical'
                  ? 'magenta'
                  : t.sla_risk === 'breach'
                    ? 'red'
                    : t.sla_risk === 'warning'
                      ? 'orange'
                      : 'green'
              }
              style={{ margin: 0, fontSize: 11 }}
            >
              SLA {formatSlaLine(t)}
            </Tag>
          ) : null}
          {(t.escalation_level ?? 0) > 0 ? <Tag color="magenta" style={{ margin: 0, fontSize: 11 }}>L{t.escalation_level}</Tag> : null}
          {t.dependency_blocked ? (
            <Tag
              color="default"
              style={{ margin: 0, fontSize: 11, cursor: t.blocking_task_ids?.[0] ? 'pointer' : undefined }}
              onClick={(e) => {
                stop(e);
                if (t.blocking_task_ids?.[0]) navigate(`/home/tasks/${t.blocking_task_ids[0]}`);
              }}
            >
              Blocked
            </Tag>
          ) : null}
        </div>
        <div className="uteam-card__foot">
          <span
            className={t.assigned_to ? 'uteam-avatar' : 'uteam-avatar uteam-avatar--un'}
            title={t.assigned_to_name || 'Unassigned'}
          >
            {t.assigned_to && monogram ? monogram : '·'}
          </span>
          <div className="uteam-quick" onClick={stop} onMouseDown={stop}>
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              aria-label="Open source"
              onClick={() => openActionLink(navigate, t.action_link)}
            />
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              aria-label="Open full page"
              onClick={() => navigate(`/home/tasks/${t.id}`)}
            />
            <Button
              type="text"
              size="small"
              icon={<MessageOutlined />}
              aria-label="Task notes and activity"
              onClick={() => handleOpenNotes(t)}
            />
            {(t.comment_count ?? 0) > 0 ? (
              <Text type="secondary" style={{ fontSize: 10 }}>
                {t.comment_count}
              </Text>
            ) : null}
          </div>
        </div>
        {slaB ? (
          <div className="uteam-sla-bar" aria-hidden>
            <div className="uteam-sla-fill" style={{ width: `${slaB.widthPct}%`, background: slaB.color }} />
          </div>
        ) : null}
        {t.remarks ? (
          <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block', marginTop: 4 }} title={t.remarks}>
            {t.remarks}
          </Text>
        ) : null}
        <div onClick={stop} onMouseDown={stop}>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={t.status}
            options={STATUS_COLUMNS.map((s) => ({ value: s, label: s }))}
            onChange={(v) => void handleStatusChange(t, v)}
          />
        </div>
        </div>
      </Dropdown>
    );
  };

  return (
    <>
      <div
        className={`uteam-board-wrap${selectedTaskIds.length > 0 ? ' uteam-board-wrap--has-bulk' : ''}`}
        role="main"
        aria-label={boardTitle}
      >
        <div className="uteam-topbar">
          <div className="uteam-topbar-left">
            <span className="uteam-board-title">{boardTitle}</span>
            {boardMetricChips.overdue > 0 ? (
              <span className="uteam-chip uteam-chip--danger">
                <span className="uteam-chip-dot" style={{ background: 'var(--noble-red)' }} />
                {boardMetricChips.overdue} overdue
              </span>
            ) : null}
            <span className="uteam-chip uteam-chip--warning">
              <span className="uteam-chip-dot" style={{ background: '#f77f00' }} />
              {boardMetricChips.inFlight} active
            </span>
            <span className="uteam-chip uteam-chip--success">
              <span className="uteam-chip-dot" style={{ background: 'var(--noble-green, #06D6A0)' }} />
              {boardMetricChips.done} done
            </span>
            {loading && tasks.length > 0 && <Spin size="small" aria-label="Updating" />}
          </div>
          <div className="uteam-topbar-right">
            {titleExtra}
            <Button
              type="default"
              icon={<SearchOutlined />}
              onClick={() => {
                setCommandQ('');
                setCommandOpen(true);
              }}
            >
              Search
            </Button>
            <Segmented
              value={view}
              onChange={(v) => setView(v as 'board' | 'table')}
              options={[
                { label: 'Board', value: 'board' },
                { label: 'Table', value: 'table' },
              ]}
            />
            {allowManualCreate && (
              <Button type="default" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                Add task
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={loading} onClick={triggerRefresh}>
              {justSynced && !loading ? '✓ Synced' : loading ? 'Syncing…' : 'Refresh'}
            </Button>
          </div>
        </div>
        {boardDescription ? (
          <div style={{ padding: '0 16px 8px' }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {boardDescription}
            </Paragraph>
          </div>
        ) : null}
        {onOverdueOnlyChange && !overdueOnly && boardMetricChips.overdue > 0 ? (
          <div className="uteam-suggest" role="status">
            <span>
              {boardMetricChips.overdue} task{boardMetricChips.overdue === 1 ? '' : 's'} overdue — filter to focus on them.
            </span>
            <Button type="default" size="small" danger onClick={() => onOverdueOnlyChange(true)}>
              Show overdue only
            </Button>
          </div>
        ) : null}
        {filterTokens.length > 0 ? (
          <div className="uteam-filters-row" role="region" aria-label="Active filters">
            <span className="uteam-filter-label">Active filters:</span>
            {filterTokens.map((ft) => (
              <span
                key={ft.key}
                className={`uteam-filter-token${
                  ft.kind === 'danger' ? ' uteam-filter-token--danger' : ft.kind === 'info' ? ' uteam-filter-token--info' : ''
                }`}
              >
                {ft.label}
                <button type="button" onClick={ft.onClear} aria-label={`Remove ${ft.label}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <div className="uteam-tools" style={{ padding: '6px 12px 8px' }}>
          <Space wrap align="center" size={[6, 6]}>
            {!hideModuleFilter && onModuleFilterChange && (
              <Select
                placeholder="Module"
                allowClear
                style={{ width: 140 }}
                value={moduleFilter || undefined}
                onChange={(v) => onModuleFilterChange(v ?? undefined)}
                options={MODULE_OPTIONS}
              />
            )}
            {onPriorityFilterChange && (
              <Select
                placeholder="Priority"
                allowClear
                style={{ width: 120 }}
                value={priorityFilter || undefined}
                onChange={(v) => onPriorityFilterChange(v ?? undefined)}
                options={PRIORITY_OPTIONS}
              />
            )}
            {onStatusFilterChange && (
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 130 }}
                value={statusFilter || undefined}
                onChange={(v) => onStatusFilterChange(v ?? undefined)}
                options={STATUS_FILTER_OPTIONS}
              />
            )}
            {onTaskTypeFilterChange && (
              <Select
                placeholder="Task type"
                allowClear
                style={{ width: 130 }}
                value={taskTypeFilter || undefined}
                onChange={(v) => onTaskTypeFilterChange(v ?? undefined)}
                options={TASK_TYPE_FILTER_OPTIONS}
              />
            )}
            {onSourceDocumentTypeFilterChange && (
              <Select
                placeholder="Source doc"
                allowClear
                style={{ width: 140 }}
                value={sourceDocumentTypeFilter || undefined}
                onChange={(v) => onSourceDocumentTypeFilterChange(v ?? undefined)}
                options={SOURCE_DOC_FILTER_OPTIONS}
              />
            )}
            {onTaskOriginFilterChange && (
              <Select
                placeholder="Origin"
                style={{ width: 150 }}
                value={taskOriginFilter ?? 'all'}
                onChange={(v) => onTaskOriginFilterChange((v as 'all' | 'system' | 'manual') || 'all')}
                options={TASK_ORIGIN_OPTIONS}
              />
            )}
            {!hideAssignedToMeToggle && onAssignedToMeChange && (
              <Space>
                <Text type="secondary">Assigned to me</Text>
                <Switch checked={!!assignedToMe} onChange={onAssignedToMeChange} />
              </Space>
            )}
            {onOverdueOnlyChange && (
              <Space>
                <Text type="secondary">Overdue only</Text>
                <Switch checked={!!overdueOnly} onChange={onOverdueOnlyChange} />
              </Space>
            )}
            {onDueTodayOnlyChange && (
              <Space>
                <Text type="secondary">Due today</Text>
                <Switch
                  checked={!!dueTodayOnly}
                  onChange={(c) => {
                    if (c && onNoDueDateOnlyChange) onNoDueDateOnlyChange(false);
                    onDueTodayOnlyChange(c);
                  }}
                />
              </Space>
            )}
            {onNoDueDateOnlyChange && (
              <Space>
                <Text type="secondary">No due date</Text>
                <Switch
                  checked={!!noDueDateOnly}
                  onChange={(c) => {
                    if (c && onDueTodayOnlyChange) onDueTodayOnlyChange(false);
                    onNoDueDateOnlyChange(c);
                  }}
                />
              </Space>
            )}
            {onOpenTasksOnlyChange && (
              <Space>
                <Text type="secondary">Open only</Text>
                <Switch
                  checked={!!openTasksOnly}
                  onChange={onOpenTasksOnlyChange}
                  disabled={!!statusFilter}
                  title={statusFilter ? 'Clear status filter to hide completed' : 'Hide Completed and Cancelled'}
                />
              </Space>
            )}
            {onUnassignedOnlyChange && (
              <Space>
                <Text type="secondary">Unassigned</Text>
                <Switch checked={!!unassignedOnly} onChange={onUnassignedOnlyChange} />
              </Space>
            )}
            {onTeamOthersOnlyChange && (
              <Space>
                <Text type="secondary">Others&apos; work</Text>
                <Switch checked={!!teamOthersOnly} onChange={onTeamOthersOnlyChange} />
              </Space>
            )}
            {onBlockedOnlyChange && (
              <Space>
                <Text type="secondary">Blocked</Text>
                <Switch checked={!!blockedOnly} onChange={onBlockedOnlyChange} />
              </Space>
            )}
            {onReadyToStartChange && (
              <Space>
                <Text type="secondary">Ready</Text>
                <Switch checked={!!readyToStart} onChange={onReadyToStartChange} />
              </Space>
            )}
            {onSlaRiskFilterChange && (
              <Select
                placeholder="SLA band"
                style={{ width: 140 }}
                value={slaRiskFilter ?? 'all'}
                onChange={(v) => onSlaRiskFilterChange((v as 'all' | UnifiedTaskSlaRisk) || 'all')}
                options={[
                  { value: 'all', label: 'All SLA' },
                  { value: 'safe', label: 'SLA safe' },
                  { value: 'warning', label: 'Near breach' },
                  { value: 'breach', label: 'Breached' },
                  { value: 'critical', label: 'Hard breach' },
                  { value: 'none', label: 'No SLA' },
                ]}
              />
            )}
            {onSortBySlaChange && (
              <Space>
                <Text type="secondary">Sort SLA</Text>
                <Switch checked={!!sortBySla} onChange={onSortBySlaChange} />
              </Space>
            )}
            {onSearchQueryChange !== undefined && (
              <Input.Search
                allowClear
                placeholder="Search tasks…"
                style={{ width: 200 }}
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                onSearch={(v) => onSearchQueryChange(v)}
              />
            )}
            {view === 'board' ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Ctrl+click select · Shift+click range
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Select rows for bulk actions
              </Text>
            )}
          </Space>
        </div>
        <div className={notesTask ? 'uteam-body uteam-body--split' : 'uteam-body'}>
          <div className="uteam-surface">
            {view === 'table' ? (
              <div className="uteam-table-card">
                <Table
                  rowKey="id"
                  size="small"
                  loading={loading}
                  dataSource={displayTasks}
                  columns={tableColumns}
                  pagination={{ pageSize: 25 }}
                  rowSelection={{
                    selectedRowKeys: selectedTaskIds,
                    onChange: (keys) => setSelectedTaskIds(keys as string[]),
                    preserveSelectedRowKeys: true,
                  }}
                />
              </div>
            ) : loading && tasks.length === 0 ? (
              <div className="uteam-kanban" role="region" aria-label="Task board" aria-busy>
                {STATUS_COLUMNS.map((col) => (
                  <div key={col} className="uteam-col uteam-col--empty">
                    <div className="uteam-col-h">
                      <span className="uteam-col-title">{col}</span>
                    </div>
                    <div className="uteam-skeleton-card">
                      <div className="uteam-skel-line" style={{ width: '70%' }} />
                      <div className="uteam-skel-line uteam-skel-line--sm" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="uteam-kanban" role="region" aria-label="Task board">
                {STATUS_COLUMNS.map((col) => {
                  const colTasks = sortedGrouped.get(col) ?? [];
                  const isEmpty = colTasks.length === 0;
                  const colRowsKey = colTasks.map((r) => r.id).join('|');
                  return (
                    <div
                      key={col}
                      className={`uteam-col${isEmpty ? ' uteam-col--empty' : ''}`}
                      role="group"
                      aria-label={`${col} tasks, ${colTasks.length} items`}
                    >
                      <div className="uteam-col-h">
                        <span className="uteam-col-title">
                          {col} · {colTasks.length}
                        </span>
                      </div>
                      {isEmpty ? (
                        <div className="uteam-col-empty" aria-hidden>
                          None
                        </div>
                      ) : (
                        <KanbanColumnList
                          columnId={col}
                          rows={colTasks}
                          rowsKey={colRowsKey}
                          renderRow={renderCard}
                          kbFocusId={kbFocusId}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {notesTask ? (
            <aside ref={detailRef} className="uteam-detail" role="complementary" aria-label="Task detail" tabIndex={-1}>
              <div className="uteam-detail__head">
                <Text ellipsis style={{ flex: 1, fontWeight: 600, fontSize: 14 }} title={notesTask.recommended_action ?? ''}>
                  {notesTask.recommended_action || notesTask.task_type}
                </Text>
                <Button type="text" icon={<CloseOutlined />} onClick={() => setNotesTask(null)} aria-label="Close panel" />
              </div>
              <div className="uteam-detail__body">
                <div className="uteam-breadcrumb">
                  {notesTask.module} · {notesTask.task_type}
                </div>
                <div className="uteam-detail__actions">
                  {notesTask.action_link ? (
                    <Button size="small" type="default" icon={<LinkOutlined />} onClick={() => openActionLink(navigate, notesTask.action_link)}>
                      Open source
                    </Button>
                  ) : null}
                  <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/home/tasks/${notesTask.id}`)}>
                    Full page
                  </Button>
                </div>
                <dl className="uteam-dl">
                  <div>
                    <dt>Source document</dt>
                    <dd>{notesTask.source_document_number || '—'}</dd>
                  </div>
                  <div>
                    <dt>Party</dt>
                    <dd>{notesTask.party_display_name || '—'}</dd>
                  </div>
                  <div>
                    <dt>Priority</dt>
                    <dd>{notesTask.priority}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{notesTask.status}</dd>
                  </div>
                  <div>
                    <dt>Due</dt>
                    <dd style={notesTask.due_date && (notesTask.overdue_days ?? 0) > 0 ? { color: 'var(--noble-red)' } : undefined}>
                      {notesTask.due_date || '—'}
                      {(notesTask.overdue_days ?? 0) > 0 ? ` (${notesTask.overdue_days}d overdue)` : null}
                    </dd>
                  </div>
                </dl>
                <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 6 }}>
                  Assignee
                </Typography.Title>
                <Space.Compact style={{ width: '100%' }}>
                  <Select
                    allowClear
                    placeholder="Unassigned"
                    style={{ flex: 1, minWidth: 0 }}
                    options={userSelectOptions}
                    value={notesAssignee}
                    onChange={(v) => setNotesAssignee(v)}
                    showSearch
                    optionFilterProp="label"
                  />
                  <Button type="primary" loading={notesAssignSaving} onClick={() => void applyNotesAssignee()}>
                    Apply
                  </Button>
                </Space.Compact>
                <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 6 }}>
                  Remarks
                </Typography.Title>
                <Input.TextArea
                  rows={3}
                  value={notesRemarksDraft}
                  onChange={(e) => setNotesRemarksDraft(e.target.value)}
                  placeholder="Internal remarks (saved on the task)"
                />
                <Button type="default" style={{ marginTop: 8 }} loading={notesRemarksSaving} onClick={() => void saveNotesRemarks()}>
                  Save remarks
                </Button>
                <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 6 }}>
                  Activity history
                </Typography.Title>
                {commentLoading ? (
                  <Spin />
                ) : (
                  <List
                    size="small"
                    dataSource={history}
                    locale={{ emptyText: 'No history yet' }}
                    renderItem={(item) => (
                      <List.Item>
                        <Space direction="vertical" size={0}>
                          <Space>
                            <Text type="secondary">{item.at}</Text>
                            <Tag color={item.event_type === 'assignment' ? 'blue' : 'green'}>{item.event_type}</Tag>
                          </Space>
                          <Text>{item.summary}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
                <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 6 }}>
                  Comments
                </Typography.Title>
                <List
                  size="small"
                  dataSource={comments}
                  locale={{ emptyText: 'No comments' }}
                  renderItem={(item) => (
                    <List.Item>
                      <Text type="secondary">{item.created_at}</Text>
                      <div>{item.body}</div>
                    </List.Item>
                  )}
                />
                <Space.Compact style={{ width: '100%', marginTop: 12 }}>
                  <Input.TextArea
                    rows={2}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment"
                  />
                </Space.Compact>
                <Button type="primary" style={{ marginTop: 8 }} onClick={() => void submitComment()}>
                  Post comment
                </Button>
              </div>
            </aside>
          ) : null}
        </div>
        {selectedTaskIds.length > 0 ? (
          <div className="uteam-bulkbar" role="toolbar" aria-label="Bulk actions for selected tasks">
            <span className="uteam-bulkbar__count">{selectedTaskIds.length} selected</span>
            <Space size="small" wrap>
              <Button size="small" type="primary" onClick={() => void runBulkStatusChange('Completed')}>
                Mark complete
              </Button>
              <Button size="small" onClick={() => void runBulkStatusChange('Escalated')}>
                Escalate
              </Button>
              <Button size="small" disabled={!meId} onClick={() => void runBulkAssignToMe()}>
                Assign to me
              </Button>
              <Button size="small" type="text" onClick={() => setSelectedTaskIds([])}>
                Clear
              </Button>
            </Space>
          </div>
        ) : null}
      </div>

      <Modal
        title="Search tasks"
        open={commandOpen}
        onCancel={() => {
          setCommandOpen(false);
          setCommandQ('');
        }}
        footer={null}
        destroyOnClose
        width={560}
        styles={{ body: { paddingTop: 12 } }}
      >
        <div className="uteam-command">
          <input
            ref={commandInputRef}
            className="uteam-command__input"
            type="search"
            value={commandQ}
            onChange={(e) => setCommandQ(e.target.value)}
            placeholder="Search tasks, documents, companies…"
            aria-label="Command palette search"
          />
          <div className="uteam-command__list" role="listbox" aria-label="Search results">
            {commandResults.length === 0 ? (
              <Text type="secondary" style={{ padding: 8, display: 'block' }}>
                No matches
              </Text>
            ) : (
              commandResults.map((row, i) => (
                <div
                  key={row.id}
                  role="option"
                  aria-selected={i === commandIdx}
                  className="uteam-command__row"
                  onClick={() => {
                    setCommandOpen(false);
                    setCommandQ('');
                    handleOpenNotes(row);
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.recommended_action || row.task_type}
                    {row.source_document_number ? ` · ${row.source_document_number}` : ''}
                    {row.party_display_name ? ` · ${row.party_display_name}` : ''}
                  </span>
                  <Tag style={{ flexShrink: 0 }}>{row.status}</Tag>
                </div>
              ))
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
            ↑↓ to move · Enter to open · Esc to close · Ctrl+K to toggle
          </Text>
        </div>
      </Modal>

      <Modal
        title="Keyboard shortcuts"
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={null}
        width={480}
        destroyOnClose
      >
        <div className="uteam-shorts">
          <div>
            <dt>Ctrl+K</dt>
            <dd>Search / command palette</dd>
          </div>
          <div>
            <dt>J / K</dt>
            <dd>Next / previous card (board)</dd>
          </div>
          <div>
            <dt>H / L</dt>
            <dd>First card in previous / next column</dd>
          </div>
          <div>
            <dt>Space</dt>
            <dd>Open task detail (when a card is focused)</dd>
          </div>
          <div>
            <dt>R</dt>
            <dd>Refresh</dd>
          </div>
          <div>
            <dt>N</dt>
            <dd>New task (when create is allowed)</dd>
          </div>
          <div>
            <dt>A / C / E</dt>
            <dd>Assign to me / Complete / Escalate (focused card)</dd>
          </div>
          <div>
            <dt>Ctrl+click · Shift+click</dt>
            <dd>Toggle card in selection · range select (board)</dd>
          </div>
          <div>
            <dt>Esc</dt>
            <dd>Clear selection (when tasks are selected and panel closed)</dd>
          </div>
          <div>
            <dt>?</dt>
            <dd>Show this help</dd>
          </div>
          <div>
            <dt>Esc</dt>
            <dd>Close panel or palette</dd>
          </div>
          <div>
            <dt>Right-click</dt>
            <dd>Card context menu (assign, status, copy link)</dd>
          </div>
        </div>
      </Modal>

      <Modal
        title="New manual task"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void submitCreate()}
        confirmLoading={createSaving}
        okText="Create"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input.TextArea rows={3} value={createTitle} onChange={(e) => setCreateTitle(e.target.value)} placeholder="What needs to be done?" />
          <Select style={{ width: '100%' }} value={createModule} onChange={setCreateModule} options={MODULE_OPTIONS} />
          <Select style={{ width: '100%' }} value={createPriority} onChange={setCreatePriority} options={PRIORITY_OPTIONS} />
          <DatePicker style={{ width: '100%' }} value={createDueDate} onChange={(d) => setCreateDueDate(d)} placeholder="Due date (optional)" />
          <Select
            allowClear
            placeholder="Assignee (optional)"
            style={{ width: '100%' }}
            options={userSelectOptions}
            value={createAssignee}
            onChange={(v) => setCreateAssignee(v)}
            showSearch
            optionFilterProp="label"
          />
        </Space>
      </Modal>
    </>
  );
};
